import { useEffect } from 'react'

/**
 * Sets the document title, description and canonical for a route.
 *
 * This app is a Create React App SPA: every route shares the single <title>
 * in public/index.html. Before 26 August 2026 that title was the CRA
 * placeholder "FinanSee Playground" on both public pages, so search engines
 * saw two identical, meaningless titles for the landing page and the plans
 * page and had no reason to rank either.
 *
 * The base tags in index.html still cover the first paint and the social
 * crawlers that do not execute JavaScript. This hook differentiates the two
 * public routes for search engines, which do render JavaScript.
 *
 * Only call it on genuinely public pages. Signed-in screens are disallowed in
 * robots.txt and should not advertise themselves.
 */
export default function useDocumentMeta({ title, description, canonical }) {
  useEffect(() => {
    if (title) document.title = title

    const setTag = (selector, create, value) => {
      if (!value) return undefined
      let el = document.head.querySelector(selector)
      const created = !el
      if (!el) {
        el = create()
        document.head.appendChild(el)
      }
      const previous = el.getAttribute(el.tagName === 'LINK' ? 'href' : 'content')
      el.setAttribute(el.tagName === 'LINK' ? 'href' : 'content', value)
      return () => {
        if (created) el.remove()
        else if (previous !== null) el.setAttribute(el.tagName === 'LINK' ? 'href' : 'content', previous)
      }
    }

    const restore = [
      setTag('meta[name="description"]', () => {
        const m = document.createElement('meta')
        m.setAttribute('name', 'description')
        return m
      }, description),
      setTag('link[rel="canonical"]', () => {
        const l = document.createElement('link')
        l.setAttribute('rel', 'canonical')
        return l
      }, canonical),
      setTag('meta[property="og:title"]', () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:title')
        return m
      }, title),
      setTag('meta[property="og:description"]', () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:description')
        return m
      }, description),
      setTag('meta[property="og:url"]', () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:url')
        return m
      }, canonical),
    ].filter(Boolean)

    return () => restore.forEach((fn) => fn())
  }, [title, description, canonical])
}
